/* eslint-disable no-constant-condition */
/* eslint-disable no-param-reassign */
import { AxiosError, AxiosResponse, isAxiosError } from 'axios';
import qs from 'qs';
import { addMinutes, format, isEqual } from 'date-fns';
import { deleteObject, ref } from 'firebase/storage';
import {
  Appointments,
  AppointmentStatus,
  DepositPaymentStatus,
  Discounts,
  ItemViews,
  Payments,
} from '@prisma/client';
import API_URL from '../apiconfig';
import {
  Appointment,
  AppointmentWithCustomerAndEmployee,
  CheckoutFailureData,
  GeneralRequest,
  LoadedUser,
} from '../interfaces';
import { RejectValues } from '../utils/form/rejectForm';
import { buildAcceptRequestBody } from '../utils/form/acceptRequestPayload';
import {
  CreateAptValues,
  ImageItem,
  isQueueWalkIn,
  removePersistedFiles,
} from '../utils/form/appointmentForm';
import { updateCustomer } from './user';
import { updateCustomerGuest } from './customers';
import { auth, storage } from '../firebase';
import client from '../utils/react-query/client';
import {
  AppointmentDepositCheckoutSession,
  CheckoutFormValues,
  MarkDepositPaidPayload,
  AcceptRequestFlowValues,
  PaymentForDepositTransfer,
  SortableKeys,
  TransferAppointmentDepositPayload,
  TransferPaymentDepositPayload,
  UnarchiveAppointmentResult,
} from '../types/appointments';
import {
  CheckoutCustomer,
  PaymentWithTransfersOrders,
} from '../types/payments';
import axios from './api';
import { getApiErrorMessage } from './apiError';
import {
  cleanupFirebaseURL,
  isNil,
  removeWhitespace,
} from '../utils/global/helper';
import { getWalkInStartDateTime } from '../helpers/appointments';
import { uploadFiles } from './global';
import { SortValues } from '../types/ui';
import { isDiscountAllowed, isTipAllowed } from '../helpers/payments';
import { CustomerMessagePayload } from '../types/global';
import {
  stripUnassignedId,
  UNASSIGNED_RESOURCE_ID,
} from '../constants/calendar';

export async function fetchPaginatedGeneralRequests({
  businessId,
  cursor,
  sort,
  deletedOnly,
  excludeDeleted,
  employeeIds,
}: {
  businessId: string;
  cursor?: string;
  sort?: SortValues<SortableKeys>;
  deletedOnly?: boolean;
  excludeDeleted?: boolean;
  employeeIds?: string[];
}) {
  const realEmployeeIds = stripUnassignedId(employeeIds);
  const response = await axios.get(
    `${API_URL}/v1/appointments/general-requests`,
    {
      params: {
        limit: 25,
        businessId,
        cursor,
        sortOrder: sort?.order,
        sortOrderBy: sort?.orderBy,
        status: deletedOnly ? 'ARCHIVED' : undefined,
        excludeDeleted,
        employeeIds: realEmployeeIds,
      },
      paramsSerializer: params =>
        qs.stringify(params, {
          arrayFormat: 'repeat',
          allowEmptyArrays: true,
        }),
    },
  );

  const { meta, result } = response.data as {
    meta: { cursor: string; hasMore: boolean; count: number };
    result: GeneralRequest[];
  };

  return {
    meta,
    generalRequests: result,
  };
}

async function fetchGeneralRequests(
  businessId: string,
  cursor?: string,
  sort?: SortValues<SortableKeys>,
  deletedOnly?: boolean,
  excludeDeleted?: boolean,
  employeeIds?: string[],
): Promise<GeneralRequest[]> {
  const allResults: GeneralRequest[] = [];
  let currentCursor = cursor;
  let hasMore = true;

  try {
    while (hasMore) {
      const { meta, generalRequests } = await fetchPaginatedGeneralRequests({
        businessId,
        cursor: currentCursor,
        sort,
        deletedOnly,
        excludeDeleted,
        employeeIds,
      });

      if (!generalRequests.length) {
        break;
      }

      allResults.push(...generalRequests);

      currentCursor = meta.cursor;
      hasMore = meta.hasMore;
    }

    return allResults;
  } catch (error) {
    throw new Error('Cannot get general requests', { cause: error });
  }
}

export async function getGeneralRequests({
  businessId,
  sort,
  deletedOnly,
  excludeDeleted,
  employeeIds,
}: {
  businessId: string;
  sort?: SortValues<SortableKeys>;
  deletedOnly?: boolean;
  employeeIds?: string[];
  excludeDeleted?: boolean;
}) {
  const appointments = await fetchGeneralRequests(
    businessId,
    undefined,
    sort,
    deletedOnly,
    excludeDeleted,
    employeeIds,
  );
  return appointments;
}

export async function fetchPaginatedAppointments({
  businessId,
  status,
  cursor,
  sort,
  employeeIds,
  isCanceledByCustomer,
  offeredTimesDeclined,
}: {
  businessId: string;
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED' | 'CANCELED';
  cursor?: string;
  sort?: SortValues<SortableKeys>;
  employeeIds?: string[];
  isCanceledByCustomer?: boolean;
  offeredTimesDeclined?: 'INCLUDE' | 'EXCLUDE';
}) {
  // Filter dropdown uses 'UNASSIGNED' as a sentinel; the API takes it as a flag.
  const includeUnassigned = !!employeeIds?.includes(UNASSIGNED_RESOURCE_ID);
  const realEmployeeIds = stripUnassignedId(employeeIds);

  const response = await axios.get(`${API_URL}/v1/appointments`, {
    params: {
      limit: 25,
      businessId,
      status: [status],
      cursor,
      sortOrder: sort?.order,
      sortOrderBy: sort?.orderBy,
      employeeIds: realEmployeeIds,
      includeUnassigned,
      isCanceledByCustomer,
      offeredTimesDeclined,
    },
    paramsSerializer: params =>
      qs.stringify(params, {
        arrayFormat: 'repeat',
        allowEmptyArrays: true,
      }),
  });

  const { meta, result } = response.data as {
    meta: { cursor: string; hasMore: boolean; count: number };
    result: Appointment[];
  };

  return {
    meta,
    appointments: result,
  };
}

async function fetchAppointments(
  businessId: string,
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED' | 'CANCELED',
  cursor?: string,
  sort?: SortValues<SortableKeys>,
  employeeIds?: string[],
  isCanceledByCustomer?: boolean,
): Promise<Appointment[]> {
  const allResults: Appointment[] = [];
  let currentCursor = cursor;
  let hasMore = true;

  try {
    while (hasMore) {
      const { meta, appointments } = await fetchPaginatedAppointments({
        businessId,
        status,
        cursor: currentCursor,
        sort,
        employeeIds,
        isCanceledByCustomer,
      });
      if (!appointments.length) {
        break;
      }

      allResults.push(...appointments);

      currentCursor = meta.cursor;
      hasMore = meta.hasMore;
    }

    return allResults;
  } catch (error) {
    throw new Error('Cannot get appointments', { cause: error });
  }
}

export function getCustomerUpdateError(err: any) {
  const errs = ['Phone number is already in use'];
  if (!('response' in err)) return 'Error updating customer detail';
  const errMsg = ((err as AxiosError)?.response?.data as any)?.message;
  // filter only the error message that is related to customer update
  if (errs.includes(errMsg)) return errMsg;
  return 'Error updating customer detail';
}

export async function getBookingRequests({
  businessId,
  status,
  sort,
  employeeIds,
  isCanceledByCustomer,
}: {
  businessId: string;
  status: 'ACCEPTED' | 'REJECTED' | 'REQUESTED' | 'ARCHIVED' | 'CANCELED';
  sort?: SortValues<SortableKeys>;
  employeeIds?: string[];
  isCanceledByCustomer?: boolean;
}) {
  const appointments = await fetchAppointments(
    businessId,
    status,
    undefined,
    sort,
    employeeIds,
    isCanceledByCustomer,
  );
  return appointments;
}

export async function getAppointments({
  businessId,
  startDate,
  endDate,
  includeCanceled = true,
  limit,
  isReversed,
  onlyIncomplete,
  useExactDates,
  customerId,
  employeeId,
  excludeOtherBusinesses,
  signal,
}: {
  businessId: string;
  startDate: Date | string;
  endDate: Date | string;
  includeCanceled?: boolean;
  limit?: number;
  isReversed?: boolean;
  onlyIncomplete?: boolean;
  useExactDates?: boolean;
  customerId?: string;
  employeeId?: string;
  excludeOtherBusinesses?: boolean;
  signal?: AbortSignal;
}) {
  const sDate =
    typeof startDate === 'string' ? startDate : format(startDate, 'yyyy-MM-dd');
  const eDate =
    typeof endDate === 'string' ? endDate : format(endDate, 'yyyy-MM-dd');

  const appointments = await axios.get(
    `${API_URL}/v1/appointments/list/${sDate}`,
    {
      params: {
        endDate: eDate,
        businessId,
        includeCanceled,
        limit,
        isReversed,
        onlyIncomplete,
        useExactDates,
        customerId,
        employeeId,
        excludeOtherBusinesses,
        includeUnassigned: true,
      },
      signal,
    },
  );

  return appointments.data.result;
}

export async function rejectAppointment(
  appointment: Appointment,
  values: RejectValues,
) {
  try {
    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/${appointment.appointmentId}`,
      {
        status: 'REJECTED',
        rejectionMessage: values.rejectionMessage,
      },
      {
        params: {
          businessId: appointment.businessId,
        },
      },
    );

    return appointmentResponse;
  } catch (error) {
    throw new Error('Cannot reject appointment', { cause: error });
  }
}

export async function rejectGeneralRequest(
  appointment: GeneralRequest,
  values: RejectValues,
  loadedUserId: string,
) {
  try {
    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/general-request/${appointment.requestId}`,
      {
        status: 'REJECTED',
        employeeId: loadedUserId,
        rejectionMessage: values.rejectionMessage,
      },
      {
        params: {
          businessId: appointment.businessId,
        },
      },
    );

    return appointmentResponse;
  } catch (error) {
    throw new Error('Cannot reject appointment', { cause: error });
  }
}

export async function initiateCheckout(
  appointment: Appointment,
  values: CheckoutFormValues,
  failureData: CheckoutFailureData | null,
  selectedDiscount?: Discounts | null,
): Promise<
  Appointment & {
    paymentIntent?: {
      id: string;
      client_secret: string;
    };
  }
> {
  // Patch update to customers
  await updateCustomer(
    {
      firstName: values.customer.firstName,
      lastName: values.customer.lastName,
      phoneNumber: values.customer.phoneNumber,
      email: values.customer.email,
      dob: values.customer.dob,
    },
    values.customer.customerId,
  ).catch(err => {
    throw new Error(getCustomerUpdateError(err));
  });

  const data: any = {
    price: values.price,
    businessId: appointment.businessId,
    paymentMethod: values.paymentMethod,
    providerCommissionType: values.providerCommissionType,
    paymentIntentId:
      values.paymentIntentId || failureData?.paymentIntentId || undefined,
    isCashTaxed: values.isCashTaxed,
  };

  if (values.paymentMethod === 'MANUAL') {
    data.paymentMethod = 'CREDIT';
    data.isManualCheckout = true;
  }

  if (values.paymentMethod === 'FREE') {
    data.isNoCharge = true;
    data.paymentMethod = 'CASH';
  }

  if (isTipAllowed(values.paymentMethod) && values.tipAmt) {
    data.tipAmt = values.tipAmt;
  }

  if (isDiscountAllowed(values.paymentMethod) && selectedDiscount) {
    data.discountId = selectedDiscount.id;
  }

  const appointmentResponse = await axios.patch(
    `${API_URL}/v1/appointments/checkout/${appointment.appointmentId}`,
    data,
  );

  return appointmentResponse?.data?.result;
}

export async function markAppointmentNoShow(
  appointmentId: string,
  noShow: boolean,
  customer?: CheckoutCustomer,
) {
  try {
    if (customer) {
      await updateCustomer(
        {
          firstName: customer.firstName,
          lastName: customer.lastName,
          phoneNumber: customer.phoneNumber,
          email: customer.email,
          dob: customer.dob,
        },
        customer.customerId,
      ).catch(err => {
        throw new Error(getCustomerUpdateError(err));
      });
    }

    const appointmentResponse = await axios.patch(
      `${API_URL}/v1/appointments/mark-no-show/${appointmentId}`,
      {
        noShow,
      },
    );

    return appointmentResponse?.data?.result as Appointments;
  } catch (error) {
    throw new Error('Cannot mark appointment as no show', { cause: error });
  }
}

export async function closeCheckout(
  appointmentId: string,
  payload: { tattooPrice: number; tipAmt: number; paymentId?: string },
) {
  const appointmentResponse = await axios.patch(
    `${API_URL}/v1/appointments/complete-checkout/${appointmentId}`,
    payload,
  );

  return appointmentResponse;
}

export async function closeOrderCheckout(orderId: string) {
  const appointmentResponse = await axios.patch(
    `${API_URL}/v1/orders/${orderId}/complete-checkout`,
  );

  return appointmentResponse?.data?.result;
}

export async function uploadApptImages(images: ImageItem[]) {
  const user = auth.currentUser;
  const authServiceId = user?.uid;
  if (!authServiceId) throw new Error('Not authenticated');

  const imgUrls = await uploadFiles(
    images.map((img, i) => ({
      id: img.id,
      url: img.url,
      path: `users/${authServiceId}/appointment_${Date.now()}_${i}`,
    })),
    {
      onSuccess: img => {
        removePersistedFiles([img.id]);
      },
    },
  );

  return imgUrls;
}

export async function deleteApptImages(
  existingImgsId: string[],
  imagesToDelete: string[],
) {
  const promises: Promise<void>[] = [];
  imagesToDelete.forEach(imgId => {
    const imgRef = ref(storage, cleanupFirebaseURL(imgId));
    promises.push(deleteObject(imgRef));
  });
  try {
    await Promise.all(promises);
  } catch (error) {
    // custom msg
    throw new Error('Error deleting images', { cause: error });
  }

  const newRefImgsId = existingImgsId?.filter(
    imgId => !imagesToDelete?.includes(imgId),
  );

  return newRefImgsId;
}

async function syncRefImages(
  existingRefImgsId: string[],
  newImages: ImageItem[] | undefined,
  imagesToDelete: string[] | undefined,
) {
  let refImgsId = existingRefImgsId;
  if (newImages?.length) {
    const uploadedImgsId = await uploadApptImages(newImages);
    if (uploadedImgsId) refImgsId = [...refImgsId, ...uploadedImgsId];
  }
  if (imagesToDelete?.length) {
    refImgsId = await deleteApptImages(refImgsId, imagesToDelete);
  }
  return refImgsId;
}

// Validation errors come back as an array of messages
function getAcceptErrorMessage(message?: string | string[]) {
  if (Array.isArray(message)) return message.join(', ');
  return message || 'Cannot accept this request';
}

export async function updateOfferedSlots(
  appointment: Pick<Appointment, 'appointmentId' | 'businessId'>,
  offeredSlots: string[],
) {
  const response = await axios.patch(
    `${API_URL}/v1/appointments/${appointment.appointmentId}`,
    {
      status: 'ACCEPTED',
      accept: { numberOfSessions: 1, offeredSlots },
    },
    { params: { businessId: appointment.businessId } },
  );

  return response.data.result;
}

export async function submitAcceptRequest(
  values: AcceptRequestFlowValues,
  request: Appointment | GeneralRequest,
  images?: ImageItem[],
) {
  const body = buildAcceptRequestBody(values);

  if (values.businessId && values.businessId !== request.businessId) {
    body.businessId = values.businessId;
  }

  if (images) {
    const existingRefImgsId = request.refImgsId ?? [];
    body.refImgsId = await syncRefImages(
      existingRefImgsId,
      images.filter(image => !image.isUploaded),
      existingRefImgsId.filter(url => !images.some(image => image.url === url)),
    );
  }
  const url =
    'requestId' in request
      ? `${API_URL}/v1/appointments/general-request/${request.requestId}`
      : `${API_URL}/v1/appointments/${request.appointmentId}`;

  const response = await axios
    .patch<{ result?: Pick<Appointment, 'appointmentId' | 'status'> }>(
      url,
      body,
      { params: { businessId: request.businessId } },
    )
    .catch(error => {
      throw new Error(getApiErrorMessage(error, 'Cannot accept this request'), {
        cause: error,
      });
    });

  // The update endpoints can return an error payload with a 200 status
  const data = response.data as {
    result?: Pick<Appointment, 'appointmentId' | 'status'>;
    message?: string | string[];
    statusCode?: number;
  };
  if (!data?.result) {
    throw new Error(getAcceptErrorMessage(data?.message));
  }

  return data.result;
}

function getWalkInSchedulingData(appointmentLengthInMinutes: string) {
  const duration = parseInt(appointmentLengthInMinutes || '60', 10);

  const tempStartDate = getWalkInStartDateTime();
  const tempEndDate = addMinutes(tempStartDate, duration);

  return {
    startDateTime: tempStartDate.toISOString(),
    endDateTime: tempEndDate.toISOString(),
    appointmentLengthInMinutes: duration,
  };
}

export async function createAppointment(
  user: LoadedUser,
  appointment: CreateAptValues,
): Promise<
  AxiosResponse<{
    result: Pick<Appointment, 'appointmentId' | 'customerId' | 'status'>;
  }>
> {
  let customerId = appointment.customerId || '';

  const apptPhoneNumber = !appointment.phoneNumber.startsWith('+')
    ? `+${appointment.phoneNumber}`
    : appointment.phoneNumber;

  if (!appointment.customerId) {
    const createCustomerPayload: any = {
      firstName: appointment.firstName,
      lastName: appointment.lastName,
      phoneNumber: apptPhoneNumber,
    };

    if (appointment.email) {
      createCustomerPayload.email = appointment.email;
    }

    if (appointment.dob) {
      createCustomerPayload.dob = appointment.dob;
    }

    const createCustomerResponse = await axios
      .post(`${API_URL}/v1/customers`, createCustomerPayload)
      .catch(error => {
        throw new Error(getApiErrorMessage(error, 'Cannot create customer'), {
          cause: error,
        });
      });

    customerId = createCustomerResponse.data.result.customerId;
  } else {
    // Patch update to customers
    await updateCustomer(
      {
        firstName: appointment.firstName,
        lastName: appointment.lastName,
        phoneNumber: apptPhoneNumber,
        email: appointment.email,
        dob: appointment.dob,
      },
      appointment.customerId,
    ).catch(err => {
      throw new Error(getCustomerUpdateError(err));
    });
  }

  appointment.customerId = customerId;

  const isQueueWalkInSubmission = isQueueWalkIn({
    isWalkIn: appointment.appointmentType === 'WALK_IN',
    user,
  });
  const status =
    !appointment.employeeId && isQueueWalkInSubmission ? 'QUEUED' : 'SCHEDULED';

  if (status === 'QUEUED') {
    appointment.employeeId = undefined;
    appointment.startDateTime = undefined;
    appointment.endDateTime = undefined;
  } else if (appointment.appointmentType === 'WALK_IN') {
    Object.assign(
      appointment,
      getWalkInSchedulingData(appointment.appointmentLengthInMinutes!),
    );
  } else if (!appointment.isBookingLink) {
    if (!appointment.startDateTime) {
      throw new Error('Start date time is required');
    }
    const tempStartDate = new Date(appointment.startDateTime);
    const tempEndDate = addMinutes(
      tempStartDate,
      parseInt(appointment.appointmentLengthInMinutes!, 10),
    );

    appointment.startDateTime = tempStartDate.toISOString();
    appointment.endDateTime = tempEndDate.toISOString();
  }

  let serviceId;
  let piercingLocationId;
  let url = `${API_URL}/v1/appointments`;
  if (!appointment.serviceId || appointment.serviceId === '0') {
    serviceId = null;
    piercingLocationId = null;
  } else {
    serviceId = parseInt(appointment.serviceId, 10);
    piercingLocationId = appointment.piercingLocationId
      ? parseInt(appointment.piercingLocationId, 10)
      : null;
    url = `${API_URL}/v1/appointments/piercing`;
  }

  delete appointment.dob;

  // upload images
  const newImages = appointment.newImages || [];
  if (newImages.length) {
    const refImgsId = await uploadApptImages(newImages);
    if (refImgsId?.length) appointment.refImgsId = refImgsId;
  }
  delete appointment.newImages;

  const appointmentLengthInMinutes = parseInt(
    appointment.appointmentLengthInMinutes!,
    10,
  );

  const payload = {
    ...appointment,
    depositPlacement: appointment.depositPlacement || undefined,
    additionalAppointments: (appointment.additionalAppointments || []).map(
      appt => ({
        ...appt,
        appointmentLengthInMinutes: appt.appointmentLengthInMinutes
          ? parseInt(appt.appointmentLengthInMinutes, 10)
          : appointmentLengthInMinutes,
      }),
    ),
    appointmentLengthInMinutes,
    serviceId,
    piercingLocationId,
    status,
    businessTripId: appointment.businessTripId || null,
  };

  if (payload.isBookingLink) {
    payload.status = 'ACCEPTED';
    payload.startDateTime = undefined;
    payload.endDateTime = undefined;
    if (payload.appointmentType === 'CONSULTATION') {
      payload.priceType = 'FIXED';
      payload.price = 0;
      payload.depositPrice = 0;
      payload.requestWaiver = false;
    }
  }

  const appointmentResponse = await axios.post(url, payload).catch(error => {
    throw new Error(getApiErrorMessage(error, 'Cannot create appointment'), {
      cause: error,
    });
  });
  return appointmentResponse as AxiosResponse<{
    result: Pick<Appointment, 'appointmentId' | 'customerId' | 'status'>;
  }>;
}

async function updateCustomerOrGuest(
  appointment: CreateAptValues,
  customerId: string,
) {
  if (appointment.customerGuestId) {
    await updateCustomerGuest({
      customerId,
      guestId: appointment.customerGuestId,
      data: {
        firstName: appointment.firstName,
        lastName: appointment.lastName,
        email: appointment.email,
        dob: appointment.dob,
      },
    }).catch(err => {
      throw new Error(getCustomerUpdateError(err));
    });
  } else {
    await updateCustomer(
      {
        firstName: appointment.firstName,
        lastName: appointment.lastName,
        phoneNumber: appointment.phoneNumber,
        email: appointment.email,
        dob: appointment.dob,
      },
      customerId,
    ).catch(err => {
      throw new Error(getCustomerUpdateError(err));
    });
  }
}

export async function updateAppointment(
  appointment: CreateAptValues,
  existingAppointment: Pick<
    Appointment | Appointments,
    | 'startDateTime'
    | 'status'
    | 'appointmentLengthInMinutes'
    | 'customerId'
    | 'appointmentId'
    | 'refImgsId'
    | 'businessId'
    | 'employeeId'
  >,
): Promise<
  AxiosResponse<{
    result: Appointment;
  }>
> {
  if (
    existingAppointment.status === 'QUEUED' &&
    appointment.appointmentType === 'WALK_IN' &&
    appointment.employeeId &&
    !appointment.startDateTime
  ) {
    Object.assign(
      appointment,
      getWalkInSchedulingData(appointment.appointmentLengthInMinutes!),
    );
  }

  if (appointment.employeeId && !appointment.startDateTime) {
    throw new Error('Start date time is required');
  }

  const newAptDate = appointment.startDateTime
    ? new Date(appointment.startDateTime)
    : null;
  const oldAptDate = existingAppointment.startDateTime
    ? new Date(existingAppointment.startDateTime!)
    : null;

  delete appointment.additionalAppointments;

  let { status } = existingAppointment;
  const isBeingRescheduled =
    newAptDate &&
    oldAptDate &&
    (!isEqual(newAptDate, oldAptDate) ||
      String(appointment.appointmentLengthInMinutes) !==
        String(existingAppointment.appointmentLengthInMinutes) ||
      String(removeWhitespace(appointment.employeeId || '')) !==
        String(removeWhitespace(existingAppointment.employeeId || '')));
  const isBeingScheduled = newAptDate && !oldAptDate;

  if (isBeingScheduled) {
    status = 'SCHEDULED';
  }

  if (isBeingRescheduled) {
    status = 'RESCHEDULED';
  }

  if (status === 'QUEUED') {
    appointment.employeeId = undefined;
    appointment.startDateTime = undefined;
    appointment.endDateTime = undefined;
  }

  if (newAptDate) {
    const tempEndDate = addMinutes(
      newAptDate,
      parseInt(appointment.appointmentLengthInMinutes!, 10),
    );

    appointment.startDateTime = newAptDate.toISOString();
    appointment.endDateTime = tempEndDate.toISOString();
  }

  let serviceId;
  let piercingLocationId;
  if (!appointment.serviceId || appointment.serviceId === '0') {
    serviceId = null;
    piercingLocationId = null;
  } else {
    serviceId = parseInt(appointment.serviceId, 10);
    piercingLocationId = appointment.piercingLocationId
      ? parseInt(appointment.piercingLocationId, 10)
      : null;
  }

  // Patch update to customer or guest profile
  await updateCustomerOrGuest(appointment, existingAppointment.customerId);

  // upload new images
  appointment.refImgsId = existingAppointment.refImgsId;
  if (appointment.newImages?.length) {
    const refImgsId = await uploadApptImages(appointment.newImages);
    if (refImgsId)
      appointment.refImgsId = [...(appointment.refImgsId ?? []), ...refImgsId];
  }
  delete appointment.newImages;

  if (appointment.refImgsId && appointment.imagesToDelete?.length) {
    const newRefImgsId = await deleteApptImages(
      appointment.refImgsId,
      appointment.imagesToDelete,
    );
    appointment.refImgsId = newRefImgsId;
  }
  delete appointment.imagesToDelete;

  const appointmentLengthInMinutes = appointment.appointmentLengthInMinutes
    ? parseInt(appointment.appointmentLengthInMinutes, 10)
    : undefined;

  const isConsultation = appointment.appointmentType === 'CONSULTATION';
  const nextBusinessId =
    appointment.businessId || existingAppointment.businessId;
  const patchPayload = {
    ...appointment,
    appointmentLengthInMinutes,
    serviceId,
    piercingLocationId,
    status,
    businessTripId: appointment.businessTripId || null,
    employeeId: appointment.employeeId || undefined,
    ...(nextBusinessId &&
      nextBusinessId !== existingAppointment.businessId && {
        businessId: nextBusinessId,
      }),
    ...(isConsultation
      ? {
          priceType: 'FIXED' as const,
          price: 0,
          depositPrice: 0,
          minPrice: 0,
          maxPrice: 0,
          hourlyPrice: 0,
          requestWaiver: false,
        }
      : {}),
  };

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/${existingAppointment.appointmentId}`,
      patchPayload,
      {
        params: {
          businessId: existingAppointment.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(getApiErrorMessage(error, 'Error updating appointment'), {
        cause: error,
      });
    });
  return appointmentResponse;
}

export async function updateRequestDetails(
  values: CreateAptValues,
  existingRequest: Appointment | GeneralRequest,
): Promise<AxiosResponse> {
  const refImgsId = await syncRefImages(
    existingRequest.refImgsId ?? [],
    values.newImages,
    values.imagesToDelete,
  );

  await updateCustomerOrGuest(values, existingRequest.customerId);

  const nextBusinessId = values.businessId || existingRequest.businessId;
  const isBusinessChanging =
    !!nextBusinessId && nextBusinessId !== existingRequest.businessId;

  const detailFields = {
    tattooLocation: values.tattooLocation,
    bodySide: values.bodySide,
    tattooSize: values.tattooSize,
    tattooColor: values.tattooColor,
    tattooDetails: values.tattooDetails,
    internalNotes: values.internalNotes,
    refImgsId,
    ...(isBusinessChanging && { businessId: nextBusinessId }),
  };

  if ('requestId' in existingRequest) {
    return axios
      .patch(
        `${API_URL}/v1/appointments/general-request/${existingRequest.requestId}/details`,
        detailFields,
      )
      .catch(error => {
        throw new Error(getApiErrorMessage(error, 'Cannot update request'), {
          cause: error,
        });
      });
  }

  const isAccepted = existingRequest.status === 'ACCEPTED';
  const piercingLocationId =
    !values.serviceId || values.serviceId === '0'
      ? null
      : values.piercingLocationId
      ? parseInt(values.piercingLocationId, 10)
      : null;
  const nextEmployeeId = values.employeeId || existingRequest.employeeId;
  const isEmployeeChanging =
    !!values.employeeId && values.employeeId !== existingRequest.employeeId;

  return axios
    .patch(`${API_URL}/v1/appointments/${existingRequest.appointmentId}`, {
      ...detailFields,
      status: existingRequest.status,
      piercingLocationId,
      ...((isBusinessChanging || isEmployeeChanging) &&
        nextEmployeeId && { employeeId: nextEmployeeId }),
      ...(isAccepted && {
        appointmentLengthInMinutes: values.appointmentLengthInMinutes
          ? parseInt(values.appointmentLengthInMinutes, 10)
          : undefined,
        depositPrice: values.depositPrice,
        priceType: values.priceType,
        price: values.price,
        hourlyPrice: values.hourlyPrice,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
      }),
    })
    .catch(error => {
      throw new Error(getApiErrorMessage(error, 'Cannot update request'), {
        cause: error,
      });
    });
}

export async function scheduleRequest(
  appointment: CreateAptValues,
  existingRequest: Appointment | GeneralRequest,
): Promise<AxiosResponse> {
  if (!appointment.startDateTime && !appointment.isBookingLink) {
    throw new Error('Start date time is required');
  }

  appointment.customerId = existingRequest.customerId;
  const isGeneralRequest = !!(existingRequest as GeneralRequest).requestId;

  // Patch update to customer or guest profile
  await updateCustomerOrGuest(appointment, appointment.customerId);

  const tempStartDate = appointment.startDateTime
    ? new Date(appointment.startDateTime)
    : null;
  const tempEndDate = tempStartDate
    ? addMinutes(
        tempStartDate,
        parseInt(appointment.appointmentLengthInMinutes!, 10),
      )
    : null;

  appointment.startDateTime = tempStartDate
    ? tempStartDate.toISOString()
    : undefined;
  appointment.endDateTime = tempEndDate ? tempEndDate.toISOString() : undefined;

  let serviceId;
  let piercingLocationId;
  if (!appointment.serviceId || appointment.serviceId === '0') {
    serviceId = null;
    piercingLocationId = null;
  } else {
    serviceId = parseInt(appointment.serviceId, 10);
    piercingLocationId = appointment.piercingLocationId
      ? parseInt(appointment.piercingLocationId, 10)
      : null;
  }

  delete appointment.dob;

  // upload new images
  appointment.refImgsId = existingRequest.refImgsId;
  if (appointment.newImages?.length) {
    const refImgsId = await uploadApptImages(appointment.newImages);
    if (refImgsId)
      appointment.refImgsId = [...(appointment.refImgsId ?? []), ...refImgsId];
  }

  // delete images
  if (appointment.refImgsId && appointment.imagesToDelete?.length) {
    const newRefImgsId = await deleteApptImages(
      appointment.refImgsId,
      appointment.imagesToDelete,
    );
    appointment.refImgsId = newRefImgsId;
  }

  const appointmentLengthInMinutes = parseInt(
    appointment.appointmentLengthInMinutes!,
    10,
  );
  const additionalAppointments = (appointment.additionalAppointments || []).map(
    appt => ({
      ...appt,
      appointmentLengthInMinutes: appt.appointmentLengthInMinutes
        ? parseInt(appt.appointmentLengthInMinutes, 10)
        : appointmentLengthInMinutes,
    }),
  );

  const payload = {
    ...appointment,
    appointmentLengthInMinutes,
    additionalAppointments,
    serviceId,
    piercingLocationId,
    status: 'SCHEDULED',
    businessTripId: appointment.businessTripId || null,
  };

  if (appointment.isBookingLink) {
    payload.status = 'ACCEPTED';
    payload.startDateTime = undefined;
    payload.endDateTime = undefined;
  }

  if (payload.appointmentType === 'CONSULTATION') {
    payload.priceType = 'FIXED';
    payload.price = 0;
    payload.depositPrice = 0;
    payload.requestWaiver = false;
  }

  if (isGeneralRequest) {
    const appointmentResponse = await axios
      .patch(
        `${API_URL}/v1/appointments/general-request/${
          (existingRequest as GeneralRequest).requestId
        }`,
        payload,
        {
          params: {
            businessId: existingRequest.businessId,
          },
        },
      )
      .catch(error => {
        throw new Error(getApiErrorMessage(error, 'Cannot schedule request'), {
          cause: error,
        });
      });
    return appointmentResponse;
  }

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/${
        (existingRequest as Appointment).appointmentId
      }`,
      payload,
      {
        params: {
          businessId: existingRequest.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(getApiErrorMessage(error, 'Cannot schedule request'), {
        cause: error,
      });
    });
  return appointmentResponse;
}

export async function getGeneralRequest(requestId: string | undefined) {
  try {
    const result = await axios.get(
      `${API_URL}/v1/appointments/general-requests/${requestId}`,
    );
    return result.data.result;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred', { cause: error });
    }
  }
}

export async function getAppointmentById(appointmentId: string | undefined) {
  try {
    const appointments = await axios.get(
      `${API_URL}/v1/appointments/${appointmentId}`,
    );
    return appointments.data.result;
  } catch (error: unknown) {
    // Check if the error is an Axios error with a response and a 404 status
    if (
      isAxiosError(error) &&
      error.response &&
      error.response.status === 404
    ) {
      return await getGeneralRequest(appointmentId);
    }
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred', { cause: error });
    }
  }
}

export async function getAppointmentTransactions(
  appointmentId: string | undefined,
) {
  try {
    const appointments = await axios.get(
      `${API_URL}/v1/appointments/transactions/${appointmentId}/v2`,
    );
    return appointments.data.result as PaymentWithTransfersOrders[];
  } catch (error: unknown) {
    // Check if the error is an Axios error with a response and a 404 status
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred', { cause: error });
    }
  }
}

export async function cancelAppointment(
  existingAppointment: Appointment,
  { depositStatus }: { depositStatus?: DepositPaymentStatus } = {},
): Promise<AxiosResponse> {
  const status = 'CANCELED';

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/${existingAppointment.appointmentId}`,
      {
        status,
        depositStatus,
      },
      {
        params: {
          businessId: existingAppointment.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(getApiErrorMessage(error, 'Cannot cancel appointment'), {
        cause: error,
      });
    });
  return appointmentResponse;
}

export async function archiveAppointment(
  existingAppointment: Appointment | GeneralRequest,
): Promise<AxiosResponse> {
  const status = 'ARCHIVED';

  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/archive/${
        (existingAppointment as Appointment).appointmentId ||
        (existingAppointment as GeneralRequest).requestId
      }`,
      { status },
      {
        params: {
          businessId: existingAppointment.businessId,
        },
      },
    )
    .catch(error => {
      throw new Error(getApiErrorMessage(error, 'Cannot archive appointment'), {
        cause: error,
      });
    });
  return appointmentResponse;
}

export async function unarchiveAppointment(
  existingAppointment: Appointment | GeneralRequest,
): Promise<
  AxiosResponse<{ message: string; result: UnarchiveAppointmentResult }>
> {
  const appointmentResponse = await axios
    .patch(
      `${API_URL}/v1/appointments/unarchive/${
        (existingAppointment as Appointment).appointmentId ||
        (existingAppointment as GeneralRequest).requestId
      }`,
      {},
    )
    .catch(error => {
      throw new Error(
        getApiErrorMessage(error, 'Cannot unarchive appointment'),
        { cause: error },
      );
    });
  return appointmentResponse;
}

export async function createAppointmentChat(
  appointment:
    | Pick<Appointment, 'appointmentId'>
    | Pick<GeneralRequest, 'requestId'>,
  loadedUser: LoadedUser,
) {
  let payload;

  if ((appointment as Appointment).appointmentId) {
    payload = {
      appointmentId: (appointment as Appointment).appointmentId,
      businessId: loadedUser.business.id,
    };
  } else if ((appointment as GeneralRequest).requestId) {
    payload = {
      generalRequestId: (appointment as GeneralRequest).requestId,
      businessId: loadedUser.business.id,
    };
  } else {
    throw new Error('Invalid appointment type');
  }

  try {
    const chatResponse = await axios.post(
      `${API_URL}/v1/appointments/chat`,
      payload,
    );
    return {
      conversation: chatResponse?.data?.result as { id: string },
      appointmentId: chatResponse?.data?.meta?.appointmentId,
      generalRequestId: chatResponse?.data?.meta?.generalRequestId,
    };
  } catch (error) {
    throw new Error('Unable to load chat', { cause: error });
  }
}

export async function sendAppointmentReminder(
  appointment: Appointment | string, // string is the appointmentId
  payload: CustomerMessagePayload,
) {
  try {
    if (!appointment) {
      throw new Error('No appointment provided');
    }
    const appointmentId =
      typeof appointment === 'string' ? appointment : appointment.appointmentId;
    const response = await axios.post(
      `${API_URL}/v1/appointments/${appointmentId}/remind`,
      payload,
    );

    return response;
  } catch (error) {
    throw new Error('Cannot resend booking link', { cause: error });
  }
}

export async function resendCheckoutLink(
  appointment: Appointment | string, // string is the appointmentId
  payload: CustomerMessagePayload,
) {
  try {
    if (!appointment) {
      throw new Error('No appointment provided');
    }
    const appointmentId =
      typeof appointment === 'string' ? appointment : appointment.appointmentId;
    const response = await axios.post(
      `${API_URL}/v1/appointments/${appointmentId}/resend-checkout-link`,
      payload,
    );

    return response;
  } catch (error) {
    throw new Error('Cannot resend booking link', { cause: error });
  }
}

export async function appendAppointmentRefImage({
  appointmentId,
  businessId,
  status,
  existingRefImgsId,
  imageUrl,
}: {
  appointmentId: string;
  businessId?: string | null;
  status: Appointment['status'];
  existingRefImgsId?: string[] | null;
  imageUrl: string;
}) {
  const response = await axios.patch(
    `${API_URL}/v1/appointments/${appointmentId}`,
    {
      // Appointment update filter requires status even for field-only patches.
      status,
      refImgsId: [...(existingRefImgsId ?? []), imageUrl],
    },
    {
      params: businessId ? { businessId } : undefined,
    },
  );
  return response.data?.result as Appointment;
}

export async function invalidateAppointment(appointmentId: string) {
  await client.invalidateQueries({ queryKey: ['appointment', appointmentId] });
}

export async function invalidateAppointments() {
  await client.invalidateQueries({ queryKey: ['appointments'] });
}

export async function markDepositPaid(
  appointmentId: string,
  { depositPaymentMethod, depositPrice }: MarkDepositPaidPayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/mark-deposit-paid/${appointmentId}`,
      {
        depositPaymentMethod,
        ...(!isNil(depositPrice) ? { depositPrice } : {}),
      },
    );
    return response;
  } catch (error) {
    throw new Error('Cannot mark deposit as paid', { cause: error });
  }
}

export async function invalidateAppointmentPayments(appointmentId: string) {
  await client.invalidateQueries({
    queryKey: ['appointment-payments', appointmentId],
    exact: false,
    type: 'active',
  });
}

export async function invalidateAvailability() {
  await Promise.all([
    client.invalidateQueries({
      queryKey: ['dayAvailableTimes'],
      exact: false,
    }),
    client.invalidateQueries({
      queryKey: ['monthAvailability'],
      exact: false,
    }),
  ]);
}

export async function getUnreadRequests(businessId?: string) {
  try {
    const response = await axios.get(`${API_URL}/v1/appointments/unreads`, {
      params: {
        businessId,
        includeGeneralRequests: true,
        includeScheduledAppointments: true,
      },
    });
    return response.data.result as {
      generalRequests: string[];
      appointments: string[];
      scheduledAppointments: string[];
    };
  } catch (error) {
    throw new Error('Cannot get unread requests', { cause: error });
  }
}

export async function markRequestAsRead(
  requestId: string,
  type: 'appointment' | 'generalRequest',
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/appointments/mark-read/${requestId}`,
      {
        type,
      },
    );
    return response.data.result as Pick<ItemViews, 'id'>;
  } catch (error) {
    throw new Error('Cannot mark request as read', { cause: error });
  }
}

export async function getAppointmentsForDepositTransfer({
  businessId,
  customerId,
}: {
  businessId: string;
  customerId: string;
}) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/appointments/deposit-transfer`,
      {
        params: {
          businessId,
          customerId,
        },
      },
    );

    return response?.data?.result as {
      from: AppointmentWithCustomerAndEmployee[];
      to: AppointmentWithCustomerAndEmployee[];
      fromPayments: PaymentForDepositTransfer[];
    };
  } catch (error) {
    throw new Error('Cannot get appointments for deposit transfer', {
      cause: error,
    });
  }
}

export async function transferAppointmentDeposit(
  payload: TransferAppointmentDepositPayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/deposit-transfer`,
      payload,
    );

    return response?.data?.result as {
      payment: Payments;
      fromAppointment: Appointments;
      toAppointment: Appointments;
    };
  } catch (error) {
    throw new Error('Cannot transfer appointment deposit', { cause: error });
  }
}

export async function transferPaymentDeposit(
  payload: TransferPaymentDepositPayload,
) {
  const response = await axios.patch(
    `${API_URL}/v1/appointments/deposit-payment-transfer`,
    payload,
  );

  return response?.data?.result as {
    payment: Payments;
    toAppointment: Appointments;
    fromAppointment: Appointments | undefined;
  };
}

export async function getAppointmentDepositCheckoutSession({
  appointmentId,
  status,
  isPriceOnly,
  depositPrice,
  isTerminal,
  providerCommissionType,
}: {
  appointmentId: string;
  status: 'schedule' | 'reschedule';
  isPriceOnly?: boolean;
  depositPrice?: number;
  isTerminal?: boolean;
  providerCommissionType?: string;
}) {
  try {
    const params = {
      appointmentId,
      status,
      isPriceOnly,
      depositPrice,
      isTerminal,
      providerCommissionType,
      paymentMethod: 'card',
    };
    const response = await axios.get(`${API_URL}/v1/payment_intent/deposit`, {
      params,
    });

    return response.data.result as AppointmentDepositCheckoutSession;
  } catch (error) {
    throw new Error('Cannot get deposit checkout session', { cause: error });
  }
}

export async function cancelDepositCheckout(appointmentId: string) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/${appointmentId}/cancel-deposit-checkout`,
    );
    return response.data.result as Pick<Appointments, 'appointmentId'>;
  } catch (error) {
    throw new Error('Cannot cancel deposit checkout', { cause: error });
  }
}

export async function forwardGeneralRequestToArtist(
  generalRequestId: string,
  employeeId: string,
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/appointments/general-request/${generalRequestId}/forward`,
      {
        employeeId,
      },
    );
    return response.data.result as Pick<Appointments, 'appointmentId'>;
  } catch (error) {
    throw new Error('Failed to forward general request to artist', {
      cause: error,
    });
  }
}

export async function changeRequestArtist(
  appointmentId: string,
  newEmployeeId: string,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/appointments/${appointmentId}/employee`,
      { newEmployeeId },
    );
    return response.data.result as Pick<Appointments, 'appointmentId'>;
  } catch (error) {
    throw new Error('Failed to change request artist', { cause: error });
  }
}

export async function uncancelAppointment(appointmentId: string) {
  const response = await axios.patch(
    `${API_URL}/v1/appointments/${appointmentId}/uncancel`,
  );
  return response.data.result as Pick<Appointments, 'appointmentId'>;
}

export async function undoRejection(appointmentId: string) {
  const response = await axios.patch(
    `${API_URL}/v1/appointments/${appointmentId}/undo-rejection`,
  );
  return response.data.result as Pick<Appointments, 'appointmentId'>;
}

export async function removeAppointmentDeposit(appointmentId: string) {
  const response = await axios.delete(
    `${API_URL}/v1/appointments/${appointmentId}/deposit`,
  );
  return response.data.result as Pick<Appointments, 'appointmentId'>;
}

export async function invalidateUnreadRequests() {
  await client.invalidateQueries({
    queryKey: ['unread-requests'],
    exact: false,
  });
}

export async function invalidateRequests(status?: AppointmentStatus) {
  const queryKey: any[] = ['requests'];
  if (status) queryKey.push({ status });
  await client.invalidateQueries({
    queryKey,
    exact: false,
  });
}

export async function invalidateDepositTransferAppointments() {
  await client.invalidateQueries({
    queryKey: ['deposit-transfer-appointments'],
    exact: false,
  });
}
